import { STALE_TIME, GC_TIME, QUERY_PRESETS } from "@/queries/queryTimeConfig";

/**
 * ZATCA API Configuration
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * This configuration handles all interactions with the ZATCA backend service.
 * The ZATCA backend is a separate NestJS microservice running on port 3333.
 *
 * Architecture:
 * - PMS (MongoDB) → Stores hotel/booking data
 * - ZATCA Backend (PostgreSQL) → Stores invoices/certificates/compliance data
 * - Integration happens via these API endpoints
 */

export const zatcaConfig = {
  /* ═══════════════════════════════════════════════════════════════════════
   * STATUS CHECK (Hotel Owner — uses JWT propertyId)
   * ═══════════════════════════════════════════════════════════════════════ */

  getZatcaStatus: {
    key: "getZatcaStatus",
    url: "/api/v1/zatca/status",
    method: "GET",
    options: {
      ...QUERY_PRESETS.STATIC,
      meta: { description: "Check ZATCA onboarding status for current property" },
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════
   * COMPLIANCE OPERATIONS (Property Onboarding & Certificates)
   * ═══════════════════════════════════════════════════════════════════════ */

  /**
   * List all onboarded EGS Units (Hotels)
   * Queries ZATCA backend for all properties that have been onboarded
   */
  listEgsUnits: {
    key: "listEgsUnits",
    url: "/api/v1/zatca/egs",
    method: "GET",
    options: {
      ...QUERY_PRESETS.STATIC,
      meta: { description: "Fetch all ZATCA-onboarded properties" },
    },
  },

  /**
   * Get properties with pending CSID (onboarded but CSID not yet issued)
   */
  getPendingCsidProperties: {
    key: "getPendingCsidProperties",
    url: "/api/v1/zatca/pending-csid",
    method: "GET",
    options: {
      staleTime: STALE_TIME.SHORT,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: true,
      meta: { description: "Fetch properties with pending CSID" },
    },
  },

  /**
   * Get properties for onboarding (mode-based filtering)
   * Returns NOT_ONBOARDED only in dev mode, all properties in live mode
   * Pms Admin: sees ALL properties across all organizations
   * Other roles: sees only properties from their organization
   */
  getPropertiesForOnboarding: {
    key: "getPropertiesForOnboarding",
    url: "/api/v1/zatca/not-onboarded",
    method: "GET",
    options: {
      staleTime: STALE_TIME.SHORT,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: true,
      networkMode: "online",
      meta: { description: "Fetch properties for ZATCA onboarding" },
    },
  },

  /**
   * Onboard a property to ZATCA
   * Generates CSR, issues compliance CSID
   */
  onboardProperty: {
    key: "onboardProperty",
    url: "/api/v1/zatca/onboard",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["listEgsUnits", "getPropertyDetails"],
      onErrorMessage: "Failed to onboard property",
      meta: { description: "Onboard property to ZATCA compliance" },
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════
   * ADMIN: PLATFORM EGS (subscription invoice signer)
   * ═══════════════════════════════════════════════════════════════════════ */

  /**
   * Get the currently-onboarded platform EGS (the cert used to sign subscription
   * invoices the platform issues to hoteliers). Returns null if not yet onboarded.
   */
  getPlatformEgs: {
    key: "getPlatformEgs",
    url: "/api/v1/zatca/platform-egs",
    method: "GET",
    options: {
      ...QUERY_PRESETS.STATIC,
      retry: false, // 404 = not onboarded yet → UI renders the form
      meta: { description: "Get the platform-owned ZATCA EGS" },
    },
  },

  /**
   * Step 1 of 3 — generate CSR + private key for the platform EGS.
   * Backend auto-generates the serial number, stamps the commonName onto the
   * admin's ServiceCredential row.
   */
  onboardPlatformEgs: {
    key: "onboardPlatformEgs",
    url: "/api/v1/zatca/platform-onboard",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["getPlatformEgs"],
      onErrorMessage: "Failed to onboard platform EGS",
      meta: { description: "Onboard the platform-owned ZATCA EGS (admin only)" },
    },
  },

  /**
   * Step 2 of 3 — issue compliance (simulation) CSID for the platform EGS.
   * Body: { otp }. OTP comes from the admin's FATOORA portal session.
   */
  issuePlatformCsid: {
    key: "issuePlatformCsid",
    url: "/api/v1/zatca/platform-issue-csid",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["getPlatformEgs"],
      onErrorMessage: "Failed to issue platform compliance CSID",
      meta: { description: "Issue compliance CSID for platform EGS (admin only)" },
    },
  },

  /**
   * Step 3 of 3 — exchange compliance CSID for production CSID. After this
   * step the platform EGS can sign real invoices in production.
   */
  issuePlatformProductionCsid: {
    key: "issuePlatformProductionCsid",
    url: "/api/v1/zatca/platform-production-csid",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["getPlatformEgs"],
      onErrorMessage: "Failed to issue platform production CSID",
      meta: { description: "Issue production CSID for platform EGS (admin only)" },
    },
  },

  /**
   * Issue Simulation CSID (Step 2 Only)
   * Completes Step 2 of onboarding without setting production status
   */
  issueSimulationCsid: {
    key: "issueSimulationCsid",
    url: "/api/v1/zatca/issue-csid",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["listEgsUnits"],
      onErrorMessage: "Failed to issue simulation CSID",
      meta: { description: "Issue simulation ZATCA certificate (Step 2)" },
    },
  },

  /**
   * Issue Production CSID (Go Live)
   * Upgrades from compliance to production environment
   */
  issueProductionCsid: {
    key: "issueProductionCsid",
    url: "/api/v1/zatca/production-csid",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["listEgsUnits"],
      onErrorMessage: "Failed to issue production CSID",
      meta: { description: "Issue production ZATCA certificate" },
    },
  },

  /**
   * Check compliance status
   * Dry-run to verify invoice compliance before submission
   */
  checkCompliance: {
    key: "checkCompliance",
    url: "/api/v1/zatca/check-compliance",
    method: "POST",
    options: {
      retry: 1,
      meta: { description: "Check invoice compliance with ZATCA" },
    },
  },

  /**
   * Submit invoice to ZATCA
   * Final step: Submit signed invoice for clearance/reporting
   */
  submitToZatca: {
    key: "submitToZatca",
    url: "/api/v1/zatca/submit",
    method: "POST",
    options: {
      retry: 2,
      invalidateKeys: ["listInvoices", "getInvoiceDetail"],
      onSuccessMessage: "Invoice submitted to ZATCA successfully",
      onErrorMessage: "Failed to submit invoice to ZATCA",
      meta: { description: "Submit invoice to ZATCA portal" },
    },
  },

  /* ═══════════════════════════════════════════════════════════════════════
   * INVOICE OPERATIONS (Generation, Signing, Submission)
   * ═══════════════════════════════════════════════════════════════════════ */

  /**
   * List invoices for a property
   * Fetches all invoices from ZATCA backend for a specific commonName
   */
  listInvoices: {
    key: "listInvoices",
    url: "/api/v1/zatca-invoice?operation=listInvoices",
    method: "GET",
    options: {
      ...QUERY_PRESETS.LIST,
      meta: { description: "Fetch invoices for property" },
    },
  },

  /**
   * Get invoice detail
   * Fetches complete invoice data including signed XML and QR code
   */
  getInvoiceDetail: {
    key: "getInvoiceDetail",
    url: (data) => `/api/v1/folio/invoices/${data.invoiceNumber}/detail`,
    method: "GET",
    options: {
      staleTime: STALE_TIME.NORMAL,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: false,
      meta: { description: "Fetch invoice details" },
    },
  },

  /**
   * Generate invoice from booking
   * Creates and signs a ZATCA-compliant invoice from a booking checkout
   */
  generateInvoice: {
    key: "generateInvoice",
    url: "/api/v1/zatca-invoice?operation=generateInvoice",
    method: "POST",
    options: {
      retry: 1,
      invalidateKeys: ["listInvoices", "getBookingDetails"],
      onSuccessMessage: "Invoice generated and signed successfully",
      onErrorMessage: "Failed to generate invoice",
      meta: { description: "Generate ZATCA invoice from booking" },
    },
  },

  /**
   * Download invoice PDF
   * Generates and downloads invoice as PDF with QR code
   */
  downloadInvoicePdf: {
    key: "downloadInvoicePdf",
    url: (data) => `/api/v1/folio/invoices/${data.invoiceNumber}/pdf`,
    method: "GET",
    options: {
      meta: { description: "Download invoice PDF" },
    },
  },

  /**
   * Get ZATCA submission response
   * Retrieves the detailed response from ZATCA after submission
   */
  getZatcaResponse: {
    key: "getZatcaResponse",
    url: "/api/v1/zatca-invoice?operation=getZatcaResponse",
    method: "GET",
    options: {
      staleTime: STALE_TIME.NORMAL,
      gcTime: GC_TIME.MEDIUM,
      refetchOnWindowFocus: false,
      meta: { description: "Fetch ZATCA submission response" },
    },
  },
};
